import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Check, AlertTriangle, X, Clock } from "lucide-react";
import BankAccountPopup from "../../popup/bankAccountPopup";
import SubscriptionPlan from "../profile-complete/subscription";
import BaseUrl from "../../../constants/BaseUrl";
import { ShowError, ShowSuccess } from "../../../utils/ToasFunction";

const SubscriptionTiles = ({ onNavigateToPayments }) => {
  const { loginSuccess } = useSelector((state) => state.auth);
  const landlordId = loginSuccess?.user?._id;

  // null = no record | "broken" = record exists but no externalAccountId | "connected" = fully linked
  const [bankStatus, setBankStatus] = useState(null);
  const [storedContactName, setStoredContactName] = useState("");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [hasPaymentPending, setHasPaymentPending] = useState(false);
  const [hasInvalidSubscription, setHasInvalidSubscription] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [showBankPopup, setShowBankPopup] = useState(false);
  const [showSubscriptionPicker, setShowSubscriptionPicker] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);

  const fetchStatus = () => {
    const headers = new Headers();
    headers.append("Authorization", `Bearer ${loginSuccess?.token}`);
    const opts = { method: "GET", headers, redirect: "follow" };

    Promise.all([
      fetch(`${BaseUrl}landlord/profile/getLandlordContactInfo/${landlordId}`, opts).then((r) => r.json()),
      fetch(`${BaseUrl}landlord/profile/getAllSubscriptions/${landlordId}`, opts).then((r) => r.json()),
    ])
      .then(([contactResult, subscriptionResult]) => {
        const stored = contactResult?.data?.stored;
        if (!stored) {
          setBankStatus(null);
        } else if (stored.externalAccountId) {
          setBankStatus("connected");
        } else {
          setBankStatus("broken");
          setStoredContactName(`${stored.firstName || ""} ${stored.lastName || ""}`.trim());
        }

        const subs = subscriptionResult?.data || [];

        // Genuinely active: status is active AND transaction is confirmed complete (Case 4)
        const genuinelyActive = subs.some(
          (s) => s.status === "active" && s._computedStatus !== "activation_pending"
        );
        setHasActiveSubscription(genuinelyActive);

        // Activation in progress: transaction exists but not yet completed
        setHasPaymentPending(
          !genuinelyActive &&
            subs.some((s) => s._computedStatus === "activation_pending")
        );

        // Cases 1 & 2: subscription marked invalid (no valid transaction found)
        setHasInvalidSubscription(subs.some((s) => s.status === "invalid"));

        setLoadingStatus(false);
      })
      .catch(() => setLoadingStatus(false));
  };

  useEffect(() => {
    if (landlordId) fetchStatus();
  }, [landlordId]);

  const handleBankSubmit = (formData) => {
    setBankLoading(true);
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const body = JSON.stringify({
      landlordId,
      firstName: loginSuccess?.user?.firstName,
      lastName: loginSuccess?.user?.lastName,
      email: loginSuccess?.user?.email,
      externalAccountData: formData,
    });

    fetch(`${BaseUrl}landlord/profile/createContactAndExternalAccountInformation`, {
      method: "POST",
      headers,
      body,
      redirect: "follow",
    })
      .then((r) => r.json())
      .then((result) => {
        setBankLoading(false);
        if (result.status === "Success" || result.status === "success" || result.status === 200) {
          ShowSuccess("Bank account connected successfully!");
          setShowBankPopup(false);
          setBankStatus("connected");
        } else {
          ShowError(result.message || "Failed to connect bank account");
        }
      })
      .catch(() => {
        setBankLoading(false);
        ShowError("Failed to connect bank account");
      });
  };

  const isConnected = bankStatus === "connected";
  const isBroken = bankStatus === "broken";

  if (loadingStatus) return null;
  if (isConnected && hasActiveSubscription) return null;

  // Payment initiated, waiting for webhook to confirm
  if (hasPaymentPending) {
    return (
      <div className="w-full mb-6 p-5 bg-white rounded-2xl shadow-md shadow-gray-200">
        <p className="text-darkColor font-popinsSemiBold text-base mb-4">Subscription Status</p>
        <div className="flex items-start gap-4 rounded-xl border-2 border-amber-400 bg-amber-50 p-4">
          <div className="mt-0.5">
            <Clock size={20} className="text-amber-500" />
          </div>
          <div className="flex-1">
            <p className="text-darkColor font-popinsSemiBold text-sm mb-1">Subscription Activation In Progress</p>
            <p className="text-paraColor font-popinsRegular text-xs">
              Your payment is being processed. Once confirmed, your subscription will be activated automatically.
              You can check the transaction status in the Payments section.
            </p>
            {onNavigateToPayments && (
              <button
                className="mt-3 text-xs font-popinsMedium text-amber-600 underline"
                onClick={onNavigateToPayments}
              >
                View in Payments
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const bankTileStyle = isConnected
    ? "border-green-400 bg-green-50 cursor-default"
    : isBroken
    ? "border-amber-400 bg-amber-50 cursor-pointer hover:bg-amber-100"
    : "border-themeColor bg-[#E6BF9F1A] cursor-pointer hover:bg-[#E6BF9F33]";

  return (
    <>
      <div className="w-full mb-6 p-5 bg-white rounded-2xl shadow-md shadow-gray-200">
        <p className="text-darkColor font-popinsSemiBold text-base mb-4">
          Complete Your Setup
        </p>
        <div className="flex gap-4">
          {/* Connect Bank Account Tile */}
          <div
            className={`flex-1 rounded-xl border-2 p-4 transition-all duration-200 ${bankTileStyle}`}
            onClick={() => {
              if (!isConnected) setShowBankPopup(true);
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-darkColor font-popinsSemiBold text-sm">
                Connect Bank Account
              </p>
              {isConnected && (
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
              {isBroken && (
                <AlertTriangle size={18} className="text-amber-500" />
              )}
            </div>
            <p className="text-paraColor font-popinsRegular text-xs">
              {isConnected
                ? "Bank account connected"
                : isBroken
                ? "Bank account could not be linked. Please try again."
                : "Link your bank account to enable subscription payments"}
            </p>
            {!isConnected && (
              <button className={`mt-3 text-xs font-popinsMedium underline ${isBroken ? "text-amber-600" : "text-themeColor"}`}>
                {isBroken ? "Try Again" : "Connect Now"}
              </button>
            )}
          </div>

          {/* Activate / Resubscribe Subscription Tile */}
          <div
            className={`flex-1 rounded-xl border-2 p-4 transition-all duration-200 ${
              hasActiveSubscription
                ? "border-green-400 bg-green-50 cursor-default"
                : hasInvalidSubscription
                ? isConnected
                  ? "border-red-400 bg-red-50 cursor-pointer hover:bg-red-100"
                  : "border-red-400 bg-red-50"
                : isConnected
                ? "border-themeColor bg-[#E6BF9F1A] cursor-pointer hover:bg-[#E6BF9F33]"
                : "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
            }`}
            onClick={() => {
              if (!hasActiveSubscription && isConnected)
                setShowSubscriptionPicker(true);
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-darkColor font-popinsSemiBold text-sm">
                {hasInvalidSubscription && !hasActiveSubscription ? "Subscription Invalid" : "Activate Subscription"}
              </p>
              {hasActiveSubscription && (
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
            </div>
            <p className="text-paraColor font-popinsRegular text-xs">
              {hasActiveSubscription
                ? "Subscription is active"
                : hasInvalidSubscription
                ? <>Your previous subscription could not be verified as no valid payment was found. Please resubscribe to continue.{!isConnected && <><br /><span className="mt-1 inline-block">Connect bank account first to continue.</span></>}</>
                : isConnected
                ? "Choose a plan to activate your subscription"
                : "Connect bank account first to activate subscription"}
            </p>
            {!hasActiveSubscription && isConnected && (
              <button className={`mt-3 text-xs font-popinsMedium underline ${hasInvalidSubscription ? "text-red-600" : "text-themeColor"}`}>
                {hasInvalidSubscription ? "Resubscribe Now" : "Choose Plan"}
              </button>
            )}
          </div>
        </div>
      </div>

      {showBankPopup && (
        <BankAccountPopup
          onClose={() => setShowBankPopup(false)}
          onSubmit={handleBankSubmit}
          loading={bankLoading}
          prefillName={
            isBroken && storedContactName
              ? storedContactName
              : `${loginSuccess?.user?.firstName} ${loginSuccess?.user?.lastName}`
          }
        />
      )}

      {showSubscriptionPicker && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="w-[95%] max-w-5xl bg-white rounded-2xl my-8 mx-auto relative">
            <button
              onClick={() => { setShowSubscriptionPicker(false); fetchStatus(); }}
              className="absolute top-4 right-4 z-10 text-darkColor/50 hover:text-darkColor"
            >
              <X size={22} />
            </button>
            <SubscriptionPlan
              allowSelect
              onBack={() => {
                setShowSubscriptionPicker(false);
                fetchStatus();
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionTiles;
